import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { HelpSupportButton } from '../components/HelpSupportButton';
import { clearAuthState, loadAuthState } from '../lib/auth-store';
import { useCurrentUser, useTenantCurrent } from '../lib/hooks';
import { canAccessPath, getActiveMembership } from '../lib/role-access';
import { isApiError } from '../lib/api';
import { Skeleton } from '../components/ui';

const PAGE_TITLES: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/locations': 'Locations',
  '/runs': 'Activity',
  '/onboarding': 'Google connection',
  '/billing': 'Billing',
  '/branding': 'Branding',
  '/users': 'Team',
  '/admin': 'Platform admin',
};

export function ProtectedLayout() {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const auth = loadAuthState();
  const { data: currentUser, isLoading, error } = useCurrentUser();
  const { data: tenant } = useTenantCurrent({ enabled: Boolean(auth.accessToken) });

  const user = currentUser ?? (isLoading ? auth.user : null);
  const activeMembership = getActiveMembership(user || null, auth.activeTenantId);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const root = document.documentElement;
    const branding = tenant?.branding;

    if (!branding) {
      root.style.removeProperty('--brand-primary');
      root.style.removeProperty('--brand-accent');
      root.style.removeProperty('--portal-title');
      return;
    }

    root.style.setProperty('--brand-primary', branding.brandColor || '#0b8f67');
    root.style.setProperty('--brand-accent', branding.accentColor || '#142019');
    root.style.setProperty('--portal-title', `'${branding.portalTitle || 'Local Presence Studio'}'`);
  }, [tenant?.branding]);

  useEffect(() => {
    const styleId = 'tenant-custom-css';
    const branding = tenant?.branding;
    const existing = document.getElementById(styleId);

    if (!branding?.customCss) {
      if (existing) {
        existing.remove();
      }
      return;
    }

    const styleEl = existing || document.createElement('style');
    styleEl.id = styleId;
    styleEl.textContent = branding.customCss;
    if (!existing) {
      document.head.appendChild(styleEl);
    }
  }, [tenant?.branding?.customCss]);

  if (!auth.accessToken) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (error && isApiError(error) && (error.status === 401 || error.status === 403)) {
    clearAuthState();
    return <Navigate to="/login" replace state={{ from: location.pathname, reason: 'Session expired' }} />;
  }

  if (error) {
    clearAuthState();
    return <Navigate to="/login" replace state={{ from: location.pathname, reason: 'Authentication check failed' }} />;
  }

  if (isLoading) {
    return (
      <div className="layout-shell">
        <div className="sidebar" aria-hidden="true">
          <Skeleton height={40} />
          <div className="grid" style={{ gap: 8, marginTop: 12 }}>
            <Skeleton height={34} />
            <Skeleton height={34} />
            <Skeleton height={34} />
            <Skeleton height={34} />
          </div>
        </div>
        <div className="layout-main">
          <div className="topbar">
            <Skeleton height={20} width={160} />
          </div>
          <main className="main-panel">
            <Skeleton height={120} />
            <Skeleton height={280} />
          </main>
        </div>
      </div>
    );
  }

  const allowed = canAccessPath({
    pathname: location.pathname,
    globalRole: user?.globalRole || null,
    tenantRole: activeMembership?.role || null,
  });

  if (!allowed) {
    return <Navigate to="/dashboard" replace />;
  }

  const pageTitle =
    Object.entries(PAGE_TITLES).find(([path]) => location.pathname.startsWith(path))?.[1] || '';
  const initials = (user?.email || '?').slice(0, 2);

  return (
    <div className={`layout-shell ${sidebarOpen ? 'sidebar-open' : ''}`}>
      <Sidebar mobileOpen={sidebarOpen} onNavigate={() => setSidebarOpen(false)} />
      <div className="layout-main">
        <header className="topbar">
          <div className="inline">
            <button
              type="button"
              className="menu-toggle"
              onClick={() => setSidebarOpen((current) => !current)}
              aria-label="Toggle navigation menu"
            >
              Menu
            </button>
            <span className="topbar-title">{pageTitle}</span>
          </div>
          <div className="topbar-meta">
            <div className="topbar-user">
              <strong>{activeMembership?.tenantName || tenant?.branding?.portalTitle || ''}</strong>
              <span>{user?.email || ''}</span>
            </div>
            <span className="avatar" aria-hidden="true">
              {initials}
            </span>
          </div>
        </header>
        <main className="main-panel" key={location.pathname}>
          <Outlet />
        </main>
      </div>
      {sidebarOpen && <button className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} aria-label="Close sidebar" />}
      <HelpSupportButton />
    </div>
  );
}
